import type {
    PlotDto,
    PlotFileBody,
    PlotFileBodyContent,
    PlotFileDto,
} from "@farm/data/dto/plot.dto";
import { PlotDtoSchema, PlotFileDtoSchema } from "@farm/data/dto/plot.dto";
import type {
    PlotFileUpload,
    PlotUpdate,
} from "@farm/domain/models/plot.model";
import {
    Plot,
    PlotFile,
    PlotUpdateSchema,
} from "@farm/domain/models/plot.model";
import type { ModelError } from "@iot-platform/core";
import {
    FormDataSerializer,
    NonRecuperableModelError,
} from "@iot-platform/core";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import type * as z from "zod";
// eslint-disable-next-line boundaries/dependencies
import type { PlotModalFormValuesValidated } from "@farm/presentation/components/modal/plot/plot-modal-form.view";

@injectable()
export class PlotMapper {
    mapPlot(plotDto: PlotDto): Result<Plot, ModelError<typeof Plot>> {
        const parsed = PlotDtoSchema.safeParse(plotDto);

        if (!parsed.success) {
            console.error(parsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return Plot.create({
            id: plotDto.id,
            name: plotDto.name,
            geoJson: plotDto.geo_json,
            documentUrl: plotDto.document_url,
            clayPercentage: plotDto.clay_percentage,
            siltPercentage: plotDto.silt_percentage,
            sandPercentage: plotDto.sand_percentage,
            stations: plotDto.stations.map((station) => ({
                id: station.id,
                name: station.name,
                code: station.code,
            })),
        }).mapErr(() => new NonRecuperableModelError());
    }

    mapFormToEdit(
        id: number,
        plot: PlotModalFormValuesValidated,
    ): Result<PlotUpdate, z.ZodError<PlotUpdate>> {
        const maybePlotUpdate = PlotUpdateSchema.safeParse({
            id,
            name: plot.name,
            sandPercentage: plot.sandPercentage,
            siltPercentage: plot.siltPercentage,
            clayPercentage: plot.clayPercentage,
            stationIds: plot.stationIds.length ? plot.stationIds : [],
        } satisfies PlotUpdate);

        return maybePlotUpdate.success
            ? ok(maybePlotUpdate.data)
            : err(maybePlotUpdate.error);
    }

    mapToUploadFileBody(plotFile: PlotFileUpload): PlotFileBody {
        const bodyContent: PlotFileBodyContent = {
            document: plotFile.file,
        };

        return FormDataSerializer.serialize(bodyContent);
    }

    mapFileResponse(
        plotFileDto: PlotFileDto,
    ): Result<PlotFile, ModelError<typeof PlotFile>> {
        const parsed = PlotFileDtoSchema.safeParse(plotFileDto);

        if (!parsed.success) {
            console.error(parsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return PlotFile.create({
            documentUrl: plotFileDto.document_url,
        }).mapErr(() => new NonRecuperableModelError());
    }
}
